<template>
  <div class="station">
    <h2 class="h4">Wo dürfen wir Sie begrüßen?</h2>
    <p class="station__subtitle">Wählen Sie aus der Karte oder verwenden Sie die Suche.</p>
    <hr />
    <div class="station__mode-switch">
      <button class="link" @click="toggleSelectionMode">
        zur {{ mapSelectionMode ? 'Listenansicht' : 'Kartenansicht' }} wechseln
      </button>
    </div>
    <template v-if="mapSelectionMode">
      <div class="station__map">
        <div class="station__search-input" @mouseover="onSearchInputHover" />
        <Map ref="map" @isZoomedin="showList = $event" />
      </div>
      <div aria-live="assertive" aria-atomic="true">
        <div class="station__message" v-if="!showList || stationCoordinates?.length > 5">
          <IconLocate color="#737380" aria-hidden="true" />
          <p>Zoomen oder nach Ort suchen</p>
          <p>Bitte Standort freigeben, weiter in die Karte zoomen oder das Suchfeld nutzen</p>
        </div>
        <div class="station__message" v-if="showList && !stationCoordinates?.length">
          <IconLogo color="#737380" aria-hidden="true" />
          <p>Kein Stützpunkt gefunden</p>
          <p>Bitte verschieben Sie den Karten-ausschnitt oder zoomen Sie hinaus</p>
        </div>
        <div class="station__list" v-if="showList && stationCoordinates?.length <= 5">
          <el-radio-group v-model="selectedStationId" @change="(e) => onSelectedStationIdChange(e)">
            <SelectionStation
              v-for="(station, index) in stationCoordinates"
              :key="station.dienststelleId"
              :station="station"
              :active="station.dienststelleId === selectedStationId"
              :divider="index !== stationCoordinates?.length - 1"
              :hovered="hoveredStation.highlight && hoveredStation.id === station.dienststelleId"
              @click="(e) => clickStationHandler(e, station.dienststelleId)"
              @mouseenter="mouseEnterStationHandler(station.dienststelleId)"
              @mouseleave="mouseLeaveStationHandler(station.dienststelleId)"
            >
              <template v-slot:action>
                <el-radio :label="station.bezeichnung" :value="station.dienststelleId" />
              </template>
            </SelectionStation>
          </el-radio-group>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="station__list-select">
        <StationListSelection :initial-station-id="activeStationId" />
        <SelectionStation v-if="activeStation" :station="activeStation" />
      </div>
    </template>
    <Navigation :isNextButtonDisabled="isNextButtonDisabled" />
  </div>
</template>

<script>
import { detectIOS } from '@/utils/misc.js'
import { trackGa4Event } from '@/utils/tracking'
import { mapGetters } from 'vuex'

import Map from '@/components/Booking/Map.vue'
import Navigation from '@/components/Booking/Navigation.vue'
import IconLocate from '@/components/icons/IconLocate.vue'
import IconLogo from '@/components/icons/IconLogo.vue'
import SelectionStation from '@/components/SelectionStation.vue'
import StationListSelection from './StationListSelection.vue'

export default {
  components: {
    IconLocate,
    IconLogo,
    Map,
    Navigation,
    SelectionStation,
    StationListSelection,
  },
  data() {
    return {
      selectedStationId: null,
      showList: false,
      mapSelectionMode: true,
    }
  },
  activated() {
    if (this.activeStationId) {
      this.selectedStationId = this.activeStationId

      // in case user went backwards and changed service or vehicle
      this.checkBookingStatus()
    }

    this.trackLandOnStep()
  },
  computed: {
    ...mapGetters([
      'stationCoordinates',
      'activeStation',
      'activeStationId',
      'hoveredStation',
      'isLoadingStationBookingInfo',
      'stationBookingInfo',
      'telephoneStationIds',
      'keineTermineStationIds',
      'unbookableStationIds',
      'services',
      'selectedServicesId',
      'selectedState',
      'selectedVehicle',
      'newVehicle',
      'kfzartId',
      'vehicleCategories',
      'allStations',
    ]),
    selectedServicesInfo() {
      const combinedServices = this.services.ausgewaehlterLandesclub?.items
        ? this.services.oesterreichweit.items.concat(this.services.ausgewaehlterLandesclub.items)
        : this.services.oesterreichweit.items
      return combinedServices.filter((serviceObject) =>
        this.selectedServicesId.includes(serviceObject.snrId)
      )
    },
    notInState() {
      // station-mappings only has landesclubId
      if (this.activeStation?.landesclubId) {
        return this.selectedState.landesclubId !== this.activeStation?.landesclubId
      } else {
        return this.selectedState.id !== this.activeStation?.bundeslandId
      }
    },
    isNextButtonDisabled() {
      return (
        this.telephoneStationIds.includes(this.selectedStationId) ||
        this.keineTermineStationIds.includes(this.selectedStationId) ||
        this.unbookableStationIds.includes(this.selectedStationId) ||
        this.notInState ||
        this.isLoadingStationBookingInfo ||
        !this.selectedStationId
      )
    },
  },
  watch: {
    activeStationId() {
      this.selectedStationId = this.activeStationId

      // TODO: in case of list selection look for station in all stations list
      let activeStation = []
      if (this.mapSelectionMode) {
        activeStation = this.stationCoordinates.filter(
          (station) => station.dienststelleId === this.activeStationId
        )
      } else {
        activeStation = this.allStations.filter(
          (station) => station.dienststelleId === this.activeStationId
        )
      }
      this.$store.commit('setActiveStation', activeStation[0])
      this.checkBookingStatus()

      if (this.activeStationId) {
        // User picks a station on the map or in the list
        trackGa4Event({
          ga4Event: 'tvg_select_content',
          ga4ParameterName: 'content_type',
          ga4ParameterValue: this.activeStation.bezeichnung,
        })
      }
    },
    stationBookingInfo: {
      deep: true,
      handler() {
        // wait for API to load responses
        if (this.stationBookingInfo.length === this.selectedServicesId.length) {
          const buchungInternetFalse = this.stationBookingInfo.some(
            (element) => !element.schalterBuchungInternet
          )

          if (buchungInternetFalse)
            this.$store.commit('addTelephoneStationIds', this.activeStationId)

          const keineTermineTrue = this.stationBookingInfo.some(
            (element) => element.schalterKeineTermine
          )

          if (keineTermineTrue) {
            this.$store.commit('addKeineTermineStationIds', this.activeStationId)
          }
        }
      },
    },
  },
  methods: {
    onSearchInputHover() {
      if (detectIOS() && this.$refs.map.checkInput()) {
        const tempInput = document.createElement('input')
        const searchInput = document.querySelector('.station__search-input')
        tempInput.style.height = 0
        tempInput.style.opacity = 0
        searchInput.appendChild(tempInput)
        tempInput.focus()

        setTimeout(() => {
          this.$refs.map.focusInput()
          searchInput.removeChild(tempInput)
        }, 500)
      }
    },
    onSelectedStationIdChange(stationId) {
      // TODO: check for disabled stations
      this.$store.commit('setActiveStationId', stationId)
    },
    clickStationHandler(e, stationId) {
      if (e.currentTarget.children[0].className.includes('selection__row--disabled')) return // disabled station

      this.$store.commit('setActiveStationId', stationId)
    },
    mouseEnterStationHandler(stationId) {
      this.$store.commit('setHoveredStation', {
        id: stationId,
        highlight: true,
      })
    },
    mouseLeaveStationHandler(stationId) {
      this.$store.commit('setHoveredStation', {
        id: stationId,
        highlight: false,
      })
    },
    checkBookingStatus() {
      this.$store.commit('clearStationBookingInfo')

      this.selectedServicesInfo.forEach((service) => {
        const item = service.dienstleistungsangebotModelList.items
          .filter((item) => item.dienststelleId === this.activeStationId)
          .filter((item) => item.kfzartList.includes(this.kfzartId))[0]

        if (!item) {
          this.$store.commit('addUnbookableStationIds', this.activeStationId)
          return
        }

        this.$store.dispatch('getStationBookingInfo', item.dienstleistungsangebotId)
        this.$store.commit('setIsLoadingStationBookingInfo', true)
      })
    },
    trackLandOnStep() {
      let vehicleType

      if (this.selectedVehicle) {
        if (this.selectedVehicle.category.toLowerCase().includes('pkw')) {
          vehicleType = 'pkw'
        } else if (this.selectedVehicle.category.toLowerCase().includes('lkw')) {
          vehicleType = 'lkw'
        } else if (this.isMotorrad(this.selectedVehicle.category)) {
          vehicleType = 'motor_cycle'
        } else {
          vehicleType = 'other'
        }
      } else if (this.newVehicle) {
        if (this.newVehicle.type === 'PKW') vehicleType = 'pkw'
        if (this.newVehicle.type === 'Motorrad') vehicleType = 'motor_cycle'
        if (this.newVehicle.type === 'LKW') vehicleType = 'lkw'
        if (this.newVehicle.type === 'Weitere') vehicleType = 'other'
      }

      // User lands on the third step of the TVG 'Stützpunkt'
      trackGa4Event({
        ga4Event: 'tvg_step_3',
        ga4ParameterName: 'vehicle_type',
        ga4ParameterValue: vehicleType,
      })
    },
    isMotorrad(category) {
      return (
        'Motorrad' ===
        Object.keys(this.vehicleCategories).find((key) =>
          this.vehicleCategories[key].map((el) => el.title).includes(category)
        )
      )
    },
    toggleSelectionMode() {
      // First, handle the transition from map to list view
      if (this.mapSelectionMode) {
        // Clear any search input container content before hiding the map
        const searchInput = document.querySelector('.station__search-input')
        if (searchInput) {
          searchInput.innerHTML = ''
        }
        this.$store.commit('setInitMap', false)
      } else {
        // When switching to map view, ensure DOM is ready before initializing map
        this.$nextTick(() => {
          // Clear the search input container to prepare for new map initialization
          const searchInput = document.querySelector('.station__search-input')
          if (searchInput) {
            searchInput.innerHTML = ''
          }

          // Ensure we have a small delay before initializing the map
          setTimeout(() => {
            this.$store.commit('setInitMap', true)
          }, 100)
        })
      }
      this.mapSelectionMode = !this.mapSelectionMode
    },
  },
}
</script>

<style lang="scss">
.station {
  $map-top-offset: 60px;

  h4 {
    margin-bottom: 4px;
  }

  &__subtitle {
    color: $color-text-secondary;
  }

  > hr {
    @include container-divider;

    @media (max-width: $breakpoint-max-mobile) {
      display: none;
    }
  }

  &__mode-switch {
    display: flex;
    margin-bottom: -$map-top-offset;
    flex-direction: row-reverse;
    @media (max-width: $breakpoint-max-tablet) {
      flex-direction: row;
      margin-bottom: 0;
    }
  }

  &__search-input {
    width: 100%;
    max-width: 350px;
    position: absolute;
    top: -$map-top-offset;

    @media (max-width: $breakpoint-max-mobile) {
      max-width: calc(100vw - 32px - 42px - 8px);
      top: 12px;
      left: 16px;
    }
  }

  &__map {
    height: 350px;
    margin: 100px 0 16px;
    position: relative;

    @media (max-width: $breakpoint-max-mobile) {
      margin: 32px -16px 16px;
    }
  }

  &__list-select {
    margin-top: $map-top-offset;
    margin-bottom: 40px;
  }

  &__message {
    @include flex-center-align;
    flex-direction: column;
    color: $color-text-secondary;
    text-align: center;
    margin-top: 28px;
    margin-bottom: 40px;

    p:first-of-type {
      font-weight: $font-weight-bold;
      margin-top: 12px;
      margin-bottom: 4px;
    }

    p:last-of-type {
      max-width: 280px;
    }
  }

  &__list {
    margin-bottom: 40px;
  }
}
</style>
